// Scene strip for a scene-reel ad in AdDetailModal (API contract §4-6).
//
// Four tiles — three lifestyle scenes and the closing product shot — each
// with its frames, clip, QC verdict, prompt and take history, and a
// "Regenerate this scene" panel. That panel is the ONLY regenerate a scene
// reel has: the whole-ad Regenerate / 720p controls are hidden for these ads
// (the backend refuses them with SCENE_REEL_REGENERATE_PER_SCENE, and a
// whole-ad re-roll would buy a full video on the wrong model).
//
// A failed reel is completed one press per scene still missing a usable take
// (contract §6): a scene that fails for good holds the scenes still mid-take.
// `failedScene` names the next one to press — the backend moves it after each
// press — so the strip leads with "regenerate scene N next", marks that tile,
// and marks the other held scenes as needing their own press. The press that
// fills the last gap re-cuts the reel.
//
// Data: the projected summary on the ad (ad.sceneReel) paints the tiles at
// once; GET /api/ads/:id/scenes fills in frames, prompts and takes. The
// detail is fetched when the strip opens and again each time `regenerating`
// turns false, so a finished scene regenerate shows its new take. A 202
// hands `regenerating:true` back to the modal, whose existing poller then
// tracks the stages (stageLabel's scene-* rows). Which one is current: a
// detail fetched since the last regenerate settled beats the summary (its
// failedScene:null included, contract §5); during a regenerate and until its
// re-fetch lands, the polled summary does, and no scene can be spent on.
//
// A settled regenerate is not a finished reel. `regenerating` clears when the
// new plate lands; the retitle it stamps runs after that, and only that
// retitle moves renderUrl (and a failed reel to draft). The projection says so
// (sceneReel.retitlePending): the modal's poll keeps going while it is true,
// and the strip re-reads GET /scenes once more when it turns false. That
// follow is bounded (RETITLE_FOLLOW_MAX_MS); once it runs out the modal says
// so (retitleFollowStopped) and the strip asks for a refresh later.
//
// MONEY copy: before a press the panel names no exact count — only the 202
// knows which frames the press can reuse. After the press the toast, and the
// tile while the press is followed, echo the 202's `billable` as sent.
//
// The 1:1 is a free crop of the 9:16 (role 'derive'): it shows the scenes
// read-only and sends the operator to the 9:16, where scenes are edited.
//
// Copy: no "master" / "size" (scene reel copy rule; see GenerateAds).

import { useEffect, useRef, useState } from 'react';
import {
  Box, VStack, HStack, Text, Badge, Button, Checkbox, Image, SimpleGrid,
  Spinner, Textarea, useToast, Collapse
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import type { ExpansionAd } from './index';
import {
  RETITLE_FOLLOW_MAX_MS,
  SCENE_REGENERATE_PROMPT_MAX,
  failedReelGuidance,
  qcGradeLabel,
  sceneListLabel,
  sceneDurationLabel,
  sceneReelBusy,
  sceneRegenerateBillableText,
  sceneRegenerateBody,
  sceneRegenerateUrl,
  sceneTitle,
  selectedTake,
  takeCostLabel,
  takeTriggerLabel,
  toSceneRegenerateBillable,
  toSceneReelDetail,
  type SceneDetail,
  type SceneReelDetail,
  type SceneReelQcGrade,
  type SceneReelSceneSummary,
  type SceneRegenerateResponse
} from '../../lib/sceneReel';

function qcColor(grade: SceneReelQcGrade | null): string {
  return grade === 'pass' ? 'green' : grade === 'minor' ? 'yellow' : grade === 'fail' ? 'red' : 'gray';
}

function statusLabel(status: SceneReelSceneSummary['status'], held = false): string {
  switch (status) {
    case 'ready':   return 'Ready';
    case 'running': return 'In progress';
    case 'failed':  return 'Failed';
    default:        return held ? 'Held' : 'Waiting';
  }
}

function shortTime(iso: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return new Date(t).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

type Tile = {
  summary: SceneReelSceneSummary;
  detail:  SceneDetail | null;
};

// On a failed reel, how many scenes would still be missing a usable take after
// a press on `index`: the next scene and the held ones, less the one pressed.
// 0 when no scene has failed — that press re-cuts the reel.
function missingAfterPress(guidance: { next: number; held: number[] } | null, index: number): number {
  if (!guidance) return 0;
  const missing = new Set([guidance.next, ...guidance.held]);
  missing.delete(index);
  return missing.size;
}

// Tiles come from the projected summary first, the detail second; a scene the
// detail knows about but the summary does not (or vice versa) still shows.
// A FRESH detail (fetched since the last regenerate settled) wins on status,
// QC and thumbnail: the summary is whatever the modal opened with, and a stale
// "failed" on a scene that has since been fixed invites a paid regenerate. A
// stale detail (a regenerate is running, or its re-fetch has not landed) only
// fills in frames, prompts and takes; the polled summary is the live one then.
function buildTiles(ad: ExpansionAd, detail: SceneReelDetail | null, detailFresh: boolean): Tile[] {
  const byIndex = new Map<number, Tile>();
  for (const s of ad.sceneReel?.scenes ?? []) byIndex.set(s.index, { summary: s, detail: null });
  for (const d of detail?.scenes ?? []) {
    const prev = byIndex.get(d.index);
    const take = selectedTake(d);
    const fromDetail: SceneReelSceneSummary = {
      index: d.index, kind: d.kind, status: d.status,
      thumbUrl: take?.clipThumbUrl ?? take?.firstFrameUrl ?? prev?.summary.thumbUrl ?? null,
      qc: take?.qc.grade ?? prev?.summary.qc ?? null,
      takes: d.takes.length || prev?.summary.takes || 0
    };
    byIndex.set(d.index, {
      summary: detailFresh || !prev ? fromDetail : prev.summary,
      detail: d
    });
  }
  return [...byIndex.values()].sort((a, b) => a.summary.index - b.summary.index);
}

export function SceneStrip({
  ad,
  brandId,
  retitleFollowStopped = false,
  onMutated,
  onOpenMaster
}: {
  ad:           ExpansionAd;
  brandId:      string;
  /** The modal stopped following a pending retitle (RETITLE_FOLLOW_MAX_MS). */
  retitleFollowStopped?: boolean;
  onMutated:    (updated: ExpansionAd) => void;
  onOpenMaster: (adId: string) => void;
}) {
  const [detail, setDetail] = useState<SceneReelDetail | null>(null);
  const [detailState, setDetailState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  // True only while `detail` was fetched after the last regenerate settled.
  // A regenerate makes it stale at once; its re-fetch makes it fresh again.
  const [detailFresh, setDetailFresh] = useState(false);
  // "Regenerate scene N" in the held-reel banner opens that tile's panel.
  const [openRequest, setOpenRequest] = useState<{ index: number; seq: number } | null>(null);
  const isRegenerating = !!ad.regenerating;
  const summary = ad.sceneReel ?? null;
  // The settled regenerate's retitle is still to land (contract §4 / §6).
  const retitlePending = !isRegenerating && summary?.retitlePending === true;
  const isDerive = summary?.role === 'derive';
  const editAdId = isDerive ? (summary?.masterAdId ?? ad.masterAdId ?? detail?.masterAdId ?? null) : null;
  // Live open-ad id — the modal reuses this component across ads.
  const openAdIdRef = useRef(ad.adId);
  openAdIdRef.current = ad.adId;

  // Fetch on open, every time a regenerate settles (regenerating → false),
  // and once more when its retitle lands (retitlePending → false). No fetch
  // while a regenerate runs; the polled summary is the live view then.
  useEffect(() => {
    if (isRegenerating) {
      setDetailFresh(false);
      return;
    }
    const startedAdId = ad.adId;
    let cancelled = false;
    setDetailState(prev => (prev === 'ready' ? 'ready' : 'loading'));
    apiJson<unknown>(`/api/ads/${encodeURIComponent(startedAdId)}/scenes?brandId=${encodeURIComponent(brandId)}`)
      .then(res => {
        if (cancelled || openAdIdRef.current !== startedAdId) return;
        const parsed = toSceneReelDetail(res);
        setDetail(parsed);
        setDetailFresh(!!parsed);
        setDetailState(parsed ? 'ready' : 'unavailable');
      })
      .catch(() => {
        if (cancelled || openAdIdRef.current !== startedAdId) return;
        setDetail(null);
        setDetailFresh(false);
        setDetailState('unavailable');
      });
    return () => { cancelled = true; };
  }, [ad.adId, brandId, isRegenerating, retitlePending]);

  // A different ad: drop the previous one's detail at once.
  useEffect(() => { setDetail(null); setDetailFresh(false); setDetailState('loading'); setOpenRequest(null); }, [ad.adId]);

  const tiles = buildTiles(ad, detail, detailFresh);
  // A fresh detail's failedScene is authoritative even when it is null (API
  // contract §5: null = no scene has failed); only a stale or missing detail
  // falls back to the projected summary.
  const failed = detail && detailFresh ? detail.failedScene : (summary?.failedScene ?? null);
  const isExported = ad.metaSyncStatus === 'synced';
  // Caps and `allowed` are read from a fresh detail only: right after a
  // regenerate settles, the previous detail still says a scene has one left.
  const regen = detailFresh ? (detail?.regenerate ?? null) : null;
  const refreshing = !isRegenerating && !!detail && !detailFresh;
  // Why no scene on this ad can be regenerated right now, or null.
  const lockReason =
    isDerive        ? 'Scenes are edited on the 9:16 version; this 1:1 is cropped from it for free.'
    : isExported    ? 'Exported ads are read-only.'
    : isRegenerating ? 'A regeneration is already in progress.'
    : retitlePending ? (retitleFollowStopped
        ? 'The reel is still being re-titled with the new scene — refresh the page later.'
        : 'The reel is being re-titled with the new scene — wait for it to finish.')
    : !regen         ? (detailState === 'unavailable' ? 'Scene regenerate is not available for this ad.' : 'Loading scene details…')
    : !regen.allowed ? (regen.reason || 'Scene regenerate is not available for this ad.')
    : regen.remainingToday === 0 ? 'Daily scene regenerate limit reached for this ad.'
    : null;
  // A failed reel's next press, and the other scenes it held (null when no
  // scene has failed). Read off the same tiles the operator sees.
  const guidance = isDerive ? null : failedReelGuidance(failed, tiles.map(t => t.summary));
  const heldSet = new Set(guidance?.held ?? []);
  // The next scene's own lock (its per-scene cap included), for the banner button.
  const nextRemaining = guidance ? (regen?.perScene.find(p => p.index === guidance.next)?.remainingToday ?? null) : null;
  const nextLock = lockReason ?? (nextRemaining === 0 ? 'Daily limit reached for this scene.' : null);
  const heldCopy = !guidance ? null
    : guidance.held.length === 0 ? 'Regenerating it completes the reel.'
    : `${sceneListLabel(guidance.held)} ${guidance.held.length === 1 ? 'was' : 'were'} held when it failed and `
      + `${guidance.held.length === 1 ? 'needs its' : 'each need their'} own regenerate — one press per scene. `
      + 'After each press this names the next one; the press that fills the last scene re-cuts the reel.';

  return (
    <VStack align="stretch" spacing={2} mb={3}>
      <HStack justify="space-between" align="baseline">
        <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
          Scenes{summary ? ` · ${summary.cut.join(' + ')} s` : ''}
        </Text>
        {(detailState === 'loading' || refreshing || (retitlePending && !retitleFollowStopped)) && <Spinner size="xs" color="brand.muted" />}
        {regen && !isDerive && regen.remainingToday != null && (
          <Text fontSize="10px" color="brand.muted">{regen.remainingToday} scene regenerate{regen.remainingToday === 1 ? '' : 's'} left today</Text>
        )}
      </HStack>

      {retitlePending && (retitleFollowStopped ? (
        <Text fontSize="11px" color="orange.700" data-testid="scene-reel-retitle-stopped">
          Still re-titling the reel with the new scene. This view stopped checking after
          {' '}{Math.round(RETITLE_FOLLOW_MAX_MS / 60_000)} minutes — refresh the page later to see the new version.
        </Text>
      ) : (
        <Text fontSize="11px" color="brand.muted">
          Re-titling the reel with the new scene — the new version shows here when it is ready.
        </Text>
      ))}

      {failed && (
        <Box p={2} borderWidth="1px" borderColor="red.300" bg="red.50" borderRadius="md" data-testid="scene-reel-held">
          <Text fontSize="sm" fontWeight="700" color="red.700">
            Scene {failed.index + 1} failed — the reel is held until it is regenerated
          </Text>
          {failed.reason && <Text fontSize="11px" color="brand.muted" mt={0.5}>{failed.reason}</Text>}
          {guidance && (isRegenerating ? (
            <Text fontSize="11px" color="brand.ink" mt={1}>
              A scene is regenerating. The next scene to regenerate, if any, shows here when it lands.
            </Text>
          ) : (
            <HStack mt={1.5} spacing={2} align="center" wrap="wrap">
              <Text fontSize="11px" color="brand.ink" flex="1" minW="200px">
                <Text as="span" fontWeight="700">Next: regenerate scene {guidance.next + 1}.</Text>{' '}
                {heldCopy}
              </Text>
              <Button
                size="xs"
                variant="brand"
                isDisabled={!!nextLock}
                title={nextLock || undefined}
                onClick={() => setOpenRequest(prev => ({ index: guidance.next, seq: (prev?.seq ?? 0) + 1 }))}
              >
                Regenerate scene {guidance.next + 1}
              </Button>
            </HStack>
          ))}
        </Box>
      )}

      {isDerive && (
        <HStack p={2} spacing={3} borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="gray.50">
          <Text fontSize="11px" color="brand.ink" flex="1">
            Edit scenes on the 9:16 version. This 1:1 is re-cropped from it for free after every change.
          </Text>
          {editAdId && (
            <Button size="xs" variant="outline" onClick={() => onOpenMaster(editAdId)}>Open the 9:16</Button>
          )}
        </HStack>
      )}

      {tiles.length === 0 ? (
        <Text fontSize="11px" color="brand.muted">
          {detailState === 'loading' ? 'Loading scenes…' : 'No scene details recorded for this ad yet.'}
        </Text>
      ) : (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
          {tiles.map(tile => (
            <SceneTile
              key={tile.summary.index}
              ad={ad}
              brandId={brandId}
              tile={tile}
              durationLabel={summary ? sceneDurationLabel(summary, tile.summary.index) : null}
              highlighted={tile.summary.status === 'failed' || failed?.index === tile.summary.index}
              nextUp={guidance?.next === tile.summary.index}
              held={heldSet.has(tile.summary.index)}
              heldAfter={missingAfterPress(guidance, tile.summary.index)}
              openSignal={openRequest?.index === tile.summary.index ? openRequest.seq : 0}
              lockReason={lockReason}
              remainingForScene={regen?.perScene.find(p => p.index === tile.summary.index)?.remainingToday ?? null}
              onMutated={onMutated}
              onOpenMaster={onOpenMaster}
            />
          ))}
        </SimpleGrid>
      )}
      {detailState === 'unavailable' && tiles.length > 0 && (
        <Text fontSize="10px" color="brand.muted">Scene details are not available right now — showing the summary only.</Text>
      )}
    </VStack>
  );
}

function SceneTile({
  ad, brandId, tile, durationLabel, highlighted, nextUp, held, heldAfter, openSignal, lockReason, remainingForScene,
  onMutated, onOpenMaster
}: {
  ad:                ExpansionAd;
  brandId:           string;
  tile:              Tile;
  durationLabel:     string | null;
  highlighted:       boolean;
  /** A failed reel's next scene to regenerate (its failedScene). */
  nextUp:            boolean;
  /** Held by the failure: no usable take, needs its own press. */
  held:              boolean;
  /** On a failed reel, scenes still without a usable take after this one's press. */
  heldAfter:         number;
  /** Bumped by the held-reel banner's button to open this tile's panel. */
  openSignal:        number;
  lockReason:        string | null;
  remainingForScene: number | null;
  onMutated:         (updated: ExpansionAd) => void;
  onOpenMaster:      (adId: string) => void;
}) {
  const toast = useToast();
  const { summary, detail } = tile;
  const take = detail ? selectedTake(detail) : null;
  const grade = take?.qc.grade ?? summary.qc;
  const findings = take?.qc.findings ?? [];
  const firstFrame = take?.firstFrameUrl ?? detail?.source?.url ?? summary.thumbUrl;
  const lastFrame = summary.kind === 'closing' ? null : (take?.lastFrameUrl ?? null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showTakes, setShowTakes] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [regenerateFrames, setRegenerateFrames] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<{ message: string; masterAdId: string | null } | null>(null);
  // This tile's last press, as the 202 counted it — echoed while the press
  // is followed. null before a press, or when the 202 sent no usable counts.
  const [pressBill, setPressBill] = useState<string | null>(null);
  const tileRef = useRef<HTMLDivElement | null>(null);

  // A leftover panel from ad A must never submit against ad B.
  useEffect(() => {
    setRegenOpen(false);
    setPrompt('');
    setRegenerateFrames(false);
    setError(null);
    setPressBill(null);
  }, [ad.adId]);

  const sceneLocked = lockReason ?? (remainingForScene === 0 ? 'Daily limit reached for this scene.' : null);

  // The held-reel banner asked for this scene: open its panel and bring it into view.
  useEffect(() => {
    if (!openSignal || sceneLocked) return;
    setRegenOpen(true);
    setError(null);
    tileRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  // Keyed on the request only; a lock that lifts later must not reopen it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  const submit = async () => {
    if (sceneLocked || submitting) return;
    const startedAdId = ad.adId;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiJson<SceneRegenerateResponse>(
        sceneRegenerateUrl(startedAdId, summary.index, brandId),
        {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(sceneRegenerateBody(prompt, regenerateFrames))
        }
      );
      onMutated({
        ...ad,
        regenerating:      true,
        regenerationStage: res?.regenerationStage || 'pending'
      });
      setRegenOpen(false);
      setPrompt('');
      setRegenerateFrames(false);
      const billable = toSceneRegenerateBillable(res?.billable);
      const bill = billable ? sceneRegenerateBillableText(billable) : null;
      setPressBill(bill);
      const next = heldAfter > 0
        ? `${heldAfter} more scene${heldAfter === 1 ? '' : 's'} still need${heldAfter === 1 ? 's' : ''} a regenerate before the reel is re-cut — the next one is named here when this one lands.`
        : 'The reel is re-cut and re-titled when the new scene lands. You can keep this open to watch.';
      toast({
        title:       `Regenerating scene ${summary.index + 1}`,
        description: bill ? `Bills ${bill}. ${next}` : next,
        status:      'info',
        duration:    heldAfter > 0 ? 6000 : 4000
      });
    } catch (e) {
      // 409 / 429 / 400 messages are shown as the backend wrote them.
      const body = (e as { body?: unknown })?.body;
      const masterAdId = body && typeof body === 'object' && typeof (body as { masterAdId?: unknown }).masterAdId === 'string'
        ? (body as { masterAdId: string }).masterAdId
        : null;
      setError({ message: e instanceof Error ? e.message : String(e), masterAdId });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      ref={tileRef}
      borderWidth={highlighted || held ? '2px' : '1px'}
      borderColor={highlighted ? 'red.400' : held ? 'orange.300' : 'brand.border'}
      borderRadius="md"
      overflow="hidden"
      bg="white"
      data-testid={`scene-tile-${summary.index}`}
    >
      <HStack spacing={1} bg="gray.900" h="96px">
        {firstFrame ? (
          <Image src={firstFrame} alt={`${sceneTitle(summary)} first frame`} h="100%" flex="1" minW={0} objectFit="cover" />
        ) : (
          <Box flex="1" h="100%" display="flex" alignItems="center" justifyContent="center" color="gray.400" fontSize="9px">
            {statusLabel(summary.status, held)}
          </Box>
        )}
        {lastFrame && (
          <Image src={lastFrame} alt={`${sceneTitle(summary)} last frame`} h="100%" flex="1" minW={0} objectFit="cover" />
        )}
      </HStack>
      <VStack align="stretch" spacing={1} p={2}>
        <HStack spacing={1} justify="space-between" align="baseline">
          <Text fontSize="11px" fontWeight="800" color="brand.ink" noOfLines={1}>{sceneTitle(summary)}</Text>
          {durationLabel && <Badge fontSize="8px" variant="subtle" colorScheme="purple" flex="none">{durationLabel}</Badge>}
        </HStack>
        <HStack spacing={1} wrap="wrap">
          {nextUp && <Badge fontSize="8px" colorScheme="red" variant="solid">Regenerate next</Badge>}
          {held && <Badge fontSize="8px" colorScheme="orange" variant="subtle">Needs a regenerate</Badge>}
          <Badge fontSize="8px" colorScheme={qcColor(grade)} variant="subtle" title={findings.join('\n') || undefined}>
            {qcGradeLabel(grade)}
          </Badge>
          <Badge fontSize="8px" colorScheme={summary.status === 'failed' ? 'red' : held ? 'orange' : 'gray'} variant="subtle">
            {statusLabel(summary.status, held)}
          </Badge>
          {take?.firstFrameGenerated && <Badge fontSize="8px" colorScheme="orange" variant="subtle">Generated frame</Badge>}
        </HStack>
        {detail?.movement && <Text fontSize="10px" color="brand.muted" noOfLines={2}>{detail.movement}</Text>}
        {findings.length > 0 && grade !== 'pass' && (
          <VStack align="stretch" spacing={0}>
            {findings.slice(0, 3).map((f, i) => (
              <Text key={i} fontSize="10px" color={grade === 'fail' ? 'red.600' : 'orange.600'}>• {f}</Text>
            ))}
          </VStack>
        )}
        {take?.clipUrl && (
          <Box as="video" src={take.clipUrl} poster={take.clipThumbUrl || undefined}
            controls muted playsInline preload="none" w="100%" borderRadius="sm" bg="black" />
        )}

        {take?.prompt && (
          <>
            <Button size="xs" variant="link" alignSelf="flex-start" onClick={() => setShowPrompt(v => !v)}>
              {showPrompt ? 'Hide prompt' : 'Show prompt'}
            </Button>
            <Collapse in={showPrompt} animateOpacity>
              <Text fontSize="10px" color="brand.ink" whiteSpace="pre-wrap" bg="gray.50" p={1.5} borderRadius="sm" maxH="160px" overflowY="auto">
                {take.prompt}
              </Text>
            </Collapse>
          </>
        )}

        {detail && detail.takes.length > 0 && (
          <>
            <Button size="xs" variant="link" alignSelf="flex-start" onClick={() => setShowTakes(v => !v)}>
              {showTakes ? 'Hide takes' : `${detail.takes.length} take${detail.takes.length === 1 ? '' : 's'}`}
            </Button>
            <Collapse in={showTakes} animateOpacity>
              <VStack align="stretch" spacing={0.5}>
                {detail.takes.map(t => (
                  <Text key={t.id} fontSize="10px" color="brand.muted">
                    {[
                      `#${t.takeNo}`,
                      takeTriggerLabel(t.trigger),
                      shortTime(t.createdAt),
                      takeCostLabel(t),
                      t.qc.grade ? qcGradeLabel(t.qc.grade) : null,
                      t.id === take?.id ? 'selected' : (t.status && t.status !== 'selected' ? t.status : null)
                    ].filter(Boolean).join(' · ')}
                  </Text>
                ))}
              </VStack>
            </Collapse>
          </>
        )}

        <Button
          size="xs"
          variant={highlighted || nextUp ? 'brand' : 'outline'}
          onClick={() => { setRegenOpen(v => !v); setError(null); }}
          isDisabled={!!sceneLocked}
          title={sceneLocked || undefined}
        >
          Regenerate this scene
        </Button>
        {regenOpen && !sceneLocked && (
          <VStack align="stretch" spacing={1.5} pt={1}>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={summary.kind === 'closing'
                ? 'Optional, e.g. "slower arc, keep the label facing camera"'
                : 'Optional, e.g. "she turns toward the camera as she walks"'}
              maxLength={SCENE_REGENERATE_PROMPT_MAX}
              rows={2}
              size="xs"
              aria-label={`Refinement for scene ${summary.index + 1}`}
            />
            <Checkbox size="sm" isChecked={regenerateFrames} onChange={(e) => setRegenerateFrames(e.target.checked)}>
              <Text fontSize="10px">Also make new first/last frames</Text>
            </Checkbox>
            <Text fontSize="10px" color="brand.muted">
              {/* No exact count here: which frames the press can reuse is the
                  backend's call (frameReuse). The 202 reports it. */}
              {regenerateFrames
                ? 'Bills up to 1 new scene clip plus new frame stills and their vision checks; the count shows when you press.'
                : 'Bills up to 1 new scene clip, plus any frame stills or vision checks it needs; the count shows when you press.'}
              {heldAfter > 0
                ? ` The reel is re-cut and re-titled once ${heldAfter === 1 ? 'the 1 other scene' : `the ${heldAfter} other scenes`} still missing a take ${heldAfter === 1 ? 'has' : 'have'} one too.`
                : ' Then the reel is re-cut and re-titled.'}
              {remainingForScene != null ? ` ${remainingForScene} left today for this scene.` : ''}
            </Text>
            <HStack justify="flex-end" spacing={1}>
              <Button size="xs" variant="ghost" onClick={() => setRegenOpen(false)} isDisabled={submitting}>Cancel</Button>
              <Button size="xs" variant="brand" onClick={() => void submit()} isLoading={submitting}>
                Regenerate scene {summary.index + 1}
              </Button>
            </HStack>
          </VStack>
        )}
        {pressBill && sceneReelBusy(ad) && (
          <Text fontSize="10px" color="brand.muted" data-testid={`scene-press-bill-${summary.index}`}>
            This press bills {pressBill}.
          </Text>
        )}
        {error && (
          <Box p={1.5} borderWidth="1px" borderColor="red.300" bg="red.50" borderRadius="sm">
            <Text fontSize="10px" color="red.700">{error.message}</Text>
            {error.masterAdId && (
              <Button size="xs" variant="link" mt={0.5} onClick={() => onOpenMaster(error.masterAdId as string)}>
                Open the 9:16
              </Button>
            )}
          </Box>
        )}
      </VStack>
    </Box>
  );
}
